import { APP_VERSION } from "../version.js";
import { locale } from "../services/LocaleService.js";
import { modal } from "../components/modal.js";

export const SettingsPage = async () => {
    // Security Check
    const user = window.user;
    if (!user) {
        window.router.navigate('/');
        return document.createTextNode('');
    }

    const container = document.createElement('div');
    container.className = 'max-w-3xl mx-auto px-4 py-8 min-h-screen bg-gray-50 dark:bg-zinc-950 transition-colors duration-300';

    container.innerHTML = `
        <header class="mb-8">
            <h1 class="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <span class="material-symbols-outlined text-purple-600">settings</span>
                ${locale.t('settings.title')}
            </h1>
            <p class="text-sm text-gray-500 mt-1">Application Settings</p>
        </header>

        <div class="space-y-6">
            <!-- 1. General Settings -->
            <section class="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-gray-100 dark:border-zinc-800 overflow-hidden">
                <div class="px-6 py-4 border-b border-gray-100 dark:border-zinc-800">
                    <h2 class="text-sm font-bold text-gray-400 uppercase tracking-wider">${locale.t('settings.general')}</h2>
                </div>
                <div class="divide-y divide-gray-100 dark:divide-zinc-800">
                    <!-- Notification -->
                    <div class="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors">
                        <div class="flex items-center gap-3">
                            <div class="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                                <span class="material-symbols-outlined">notifications</span>
                            </div>
                            <div>
                                <p class="font-bold text-gray-900 dark:text-white">${locale.t('settings.push_title')}</p>
                                <p class="text-xs text-gray-500">${locale.t('settings.push_desc')}</p>
                            </div>
                        </div>
                        <div class="flex flex-col items-end gap-1">
                            <label class="relative inline-flex items-center cursor-pointer">
                                <input type="checkbox" id="toggle-notification" class="sr-only peer">
                                <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-zinc-600 peer-checked:bg-blue-600"></div>
                            </label>
                        </div>

                    <!-- Language Setting -->
                    <div id="setting-lang-row" class="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors cursor-pointer">
                        <div class="flex items-center gap-3">
                            <div class="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                                <span class="material-symbols-outlined">language</span>
                            </div>
                            <div>
                                <p class="font-bold text-gray-900 dark:text-white">${locale.t('menu.language')}</p>
                                <p class="text-xs text-gray-500">${locale.getCurrentLanguage() === 'ko' ? '한국어' : 'English'}</p>
                            </div>
                        </div>
                        <div class="flex items-center gap-2">
                             <span class="text-sm font-bold text-violet-600">${locale.getCurrentLanguage().toUpperCase()}</span>
                             <span class="material-symbols-outlined text-gray-400">chevron_right</span>
                        </div>
                    </div>
                    </div>

                    <!-- App Info -->
                    <div class="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors">
                         <div class="flex items-center gap-3">
                            <div class="w-10 h-10 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center">
                                <span class="material-symbols-outlined">info</span>
                            </div>
                            <div>
                                <p class="font-bold text-gray-900 dark:text-white">${locale.t('settings.app_info')}</p>
                                <p class="text-xs text-gray-500">${locale.t('settings.current_version')}</p>
                            </div>
                        </div>
                        <span class="px-3 py-1 rounded-full bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-zinc-400 text-xs font-bold font-mono">
                            ${APP_VERSION}
                        </span>
                    </div>
                </div>
            </section>

             <!-- 2. Account Zone (Danger) -->
            <section class="bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-100 dark:border-red-900/30 overflow-hidden mt-8">
                 <div class="px-6 py-4 border-b border-red-100 dark:border-red-900/30">
                    <h2 class="text-sm font-bold text-red-500 uppercase tracking-wider flex items-center gap-2">
                        <span class="material-symbols-outlined text-sm">warning</span> ${locale.t('settings.danger_zone')}
                    </h2>
                </div>
                <div class="p-6">
                    <div class="flex items-center justify-between">
                        <div>
                             <p class="font-bold text-red-700 dark:text-red-400">${locale.t('settings.delete_account_title')}</p>
                             <p class="text-xs text-red-500/80 mt-1">${locale.t('settings.delete_account_desc')}</p>
                        </div>
                        <button id="btn-delete-account" class="px-4 py-2 bg-white dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl text-sm font-bold shadow-sm hover:bg-red-50 dark:hover:bg-red-900/40 transition-all active:scale-95 flex items-center gap-2">
                            <span class="material-symbols-outlined text-[18px]">logout</span>
                            ${locale.t('settings.delete_account_button')}
                        </button>
                    </div>
                </div>
            </section>
        </div>
    `;

    // Dynamic Logic
    const toggleNotif = container.querySelector('#toggle-notification');

    // Check initial permission AND local preference
    if ('Notification' in window) {
        const isEnabled = localStorage.getItem('push_enabled') === 'true';
        if (Notification.permission === 'granted' && isEnabled) {
            toggleNotif.checked = true;
        }

        toggleNotif.onchange = async () => {
            if (toggleNotif.checked) {
                try {
                    // Import Service Dynamically
                    const { PushNotificationService } = await import("../services/push-notification.service.js");

                    // (Ensure userId is available)
                    if (window.user && window.user.uid) {
                        const token = await PushNotificationService.requestAndSaveToken(window.user.uid);
                        if (!token) {
                            toggleNotif.checked = false;
                            localStorage.setItem('push_enabled', 'false'); // Sync state
                            alert("알림 권한을 승인하지 않았거나 토큰 생성에 실패했습니다.");
                        } else {
                            // [FIX] Persist State
                            console.log("Token registered:", token);
                            localStorage.setItem('push_enabled', 'true');

                            // Simple Feedback
                            // alert("알림이 켜졌습니다."); // Optional, maybe too intrusive
                        }
                    } else {
                        alert("로그인이 필요합니다.");
                        toggleNotif.checked = false;
                        localStorage.setItem('push_enabled', 'false');
                    }

                } catch (e) {
                    console.error(e);
                    toggleNotif.checked = false;
                    localStorage.setItem('push_enabled', 'false');
                    alert("알림 설정 중 오류가 발생했습니다.");
                }
            } else {
                // [FIX] Handle Uncheck -> Persist "Off"
                localStorage.setItem('push_enabled', 'false');
                console.log("Push notifications disabled (UI state only).");
            }
        };
    } else {
        // Notification API not supported
        toggleNotif.disabled = true;
        toggleNotif.parentElement.title = "이 기기에서는 지원되지 않습니다.";
    }

    // Language Switcher Logic
    const langRow = container.querySelector('#setting-lang-row');
    if (langRow) {
        langRow.onclick = async () => {
            const current = locale.getCurrentLanguage();
            const selected = await modal.showLanguageSelector(current);
            if (selected && selected !== current) {
                locale.setLanguage(selected);
                // Page reloads in setLanguage, so no UI update needed here
            }
        };
    }

    // Bind Delete Account
    container.querySelector('#btn-delete-account').onclick = async () => {
        const { DeleteAccountModal } = await import("../components/modals/DeleteAccountModal.js");
        DeleteAccountModal.show();
    };

    return container;
};
